<!-- eslint-disable @typescript-eslint/no-magic-numbers -->
<template>
    <swiper
        :slides-per-view="1.2"
        :space-between="16"
        :pagination="paginationOptions"
        :modules="[Pagination]"
        :breakpoints="{
            1024: {
                slidesPerView: 2.3,
            },
            1440: {
                slidesPerView: 4.1,
            },
        }"
        @swiper="onSwiper"
        @slideChange="onSlideChange">
        <header class="card-carousel__header">
            <div class="title h4">{{ title }}</div>
            <div class="controls">
                <navigation-button
                    :item="getItem(prevSlideIndex)"
                    direction="prev"></navigation-button>
                <navigation-button
                    :item="getItem(nextSlideIndex)"
                    direction="next"></navigation-button>
            </div>
        </header>
        <swiper-slide v-for="(slide, index) in slides" :key="`slide-${index}`">
            <template v-if="slide.Link.Url">
                <a
                    :href="slide.Link.Url"
                    :title="slide.Link.Text"
                    :target="slide.Link.Target"
                    class="slide">
                    <slide-item :slide="slide"></slide-item>
                </a>
            </template>
            <template v-else>
                <div class="slide">
                    <slide-item :slide="slide"></slide-item>
                </div>
            </template>
        </swiper-slide>

        <div class="pagination"></div>
    </swiper>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import type { Ref } from 'vue';
import { ISlide } from './interface/card-carousel.model';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { PaginationOptions, Swiper as SwiperT } from 'swiper/types';

import navigationButton from './components/navigation-button.component.vue';
import slideItem from './components/slideItem.component.vue';

export default defineComponent({
    components: {
        Swiper,
        SwiperSlide,
        'navigation-button': navigationButton,
        'slide-item': slideItem,
    },
    props: {
        slides: {
            type: Array as PropType<Array<ISlide>>, //https://forum.vuejs.org/t/specifying-array-type-of-prop-in-vue-3-0-with-typescript/103169
            required: true,
        },
        title: {
            type: String,
            required: false,
        },
    },
    setup(props) {
        const activeSlideIndex: Ref<number> = ref(0);
        const nextSlideIndex: Ref<number> = ref(0);
        const prevSlideIndex: Ref<number> = ref(0);
        const activeSlide: Ref<number> = ref(0);

        const paginationOptions: PaginationOptions = {
            clickable: true,
            el: '.pagination',
        };

        const onSwiper = (swiper: SwiperT) => {
            activeSlideIndex.value = swiper.realIndex;
            nextSlideIndex.value = activeSlideIndex.value++;
            prevSlideIndex.value = activeSlideIndex.value--;
        };

        const onSlideChange = (e: any) => {
            if (e.slides[e.realIndex] !== activeSlide.value) {
                activeSlideIndex.value = getIndex(e.realIndex);
                nextSlideIndex.value = getIndex(e.realIndex + 1);
                prevSlideIndex.value = getIndex(e.realIndex - 1);
            }
            activeSlide.value = e.slides[e.realIndex];
        };

        const getIndex = (index: number) => {
            if (index < 0) {
                return props.slides.length - 1;
            }
            if (index >= props.slides.length) {
                return 0;
            }
            return index;
        };
        const getItem = (index: number) => {
            let realIndex = index;
            if (index < 0 || index >= props.slides.length) {
                realIndex = getIndex(index);
            }
            return props.slides[realIndex];
        };

        return {
            activeSlideIndex,
            nextSlideIndex,
            prevSlideIndex,
            paginationOptions,
            onSwiper,
            onSlideChange,
            getItem,
            getIndex,
            Pagination,
        };
    },
});
</script>
